import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import Sidebar from "@/components/Sidebar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Package, 
  Menu,
  Plus,
  Pencil,
  Trash2,
  Database,
  ShieldX,
  FileText,
  Clock,
  Building2,
  Mail,
  Wrench,
  Cpu,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("requests");
  const [bundles, setBundles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [offerRequests, setOfferRequests] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  // Device form state
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceForm, setDeviceForm] = useState({
    name: "", category: "laptop", brand: "", description: "",
    base_price: "", market_price: "", apple_care_price: "",
    mdm_compatible: true, mdm_monthly_price: "35", image_url: "", is_active: true
  });

  // Bundle form state
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const [bundleForm, setBundleForm] = useState({
    name: "",
    description: "",
    category: "",
    base_price: "",
    market_price: "",
    lead_time_days: "",
    delivery_days: "",
    is_recommended: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bundlesRes, packagesRes, requestsRes, srRes, devicesRes] = await Promise.all([
        axios.get(`${API}/bundles`, { withCredentials: true }),
        axios.get(`${API}/support-packages`, { withCredentials: true }),
        axios.get(`${API}/admin/offer-requests`, { withCredentials: true }).catch(() => ({ data: { requests: [] } })),
        axios.get(`${API}/admin/service-requests`, { withCredentials: true }).catch(() => ({ data: { service_requests: [] } })),
        axios.get(`${API}/admin/devices`, { withCredentials: true }).catch(() => ({ data: { devices: [] } }))
      ]);
      setBundles(bundlesRes.data.bundles);
      setPackages(packagesRes.data.packages);
      setOfferRequests(requestsRes.data.requests || []);
      setServiceRequests(srRes.data.service_requests || []);
      setDevices(devicesRes.data.devices || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/admin/seed`, {}, { withCredentials: true });
      toast.success("Sample data seeded successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to seed data");
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateBundle = async () => {
    try {
      const payload = {
        ...bundleForm,
        base_price: parseFloat(bundleForm.base_price),
        market_price: parseFloat(bundleForm.market_price),
        lead_time_days: parseInt(bundleForm.lead_time_days),
        items: [],
        features: []
      };
      
      await axios.post(`${API}/admin/bundles`, payload, { withCredentials: true });
      toast.success("Bundle created successfully!");
      setBundleDialogOpen(false);
      setBundleForm({
        name: "",
        description: "",
        category: "",
        base_price: "",
        market_price: "",
        lead_time_days: "",
        delivery_days: "",
        is_recommended: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create bundle");
    }
  };

  const categories = [
    "Business Laptops",
    "Tablet Solutions",
    "Office Setup",
    "Cybersecurity",
    "Home Office",
    "Desktops"
  ];

  // Device handlers
  const openDeviceDialog = (device = null) => {
    setEditingDevice(device);
    setDeviceForm(device ? {
      name: device.name, category: device.category, brand: device.brand || "",
      description: device.description || "", base_price: device.base_price,
      market_price: device.market_price, apple_care_price: device.apple_care_price || "",
      mdm_compatible: device.mdm_compatible, mdm_monthly_price: device.mdm_monthly_price || 35,
      image_url: device.image_url || "", is_active: device.is_active
    } : {
      name: "", category: "laptop", brand: "", description: "",
      base_price: "", market_price: "", apple_care_price: "",
      mdm_compatible: true, mdm_monthly_price: "35", image_url: "", is_active: true
    });
    setDeviceDialogOpen(true);
  };

  const handleSaveDevice = async () => {
    if (!deviceForm.name || !deviceForm.base_price || !deviceForm.market_price) {
      toast.error("Name, base price and market price are required");
      return;
    }
    try {
      if (editingDevice) {
        await axios.put(`${API}/admin/devices/${editingDevice.device_id}`, deviceForm, { withCredentials: true });
        toast.success("Device updated");
      } else {
        await axios.post(`${API}/admin/devices`, deviceForm, { withCredentials: true });
        toast.success("Device created");
      }
      setDeviceDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save device");
    }
  };

  const handleToggleDevice = async (device) => {
    try {
      await axios.put(`${API}/admin/devices/${device.device_id}`, { is_active: !device.is_active }, { withCredentials: true });
      toast.success(device.is_active ? "Device deactivated" : "Device activated");
      fetchData();
    } catch {
      toast.error("Failed to update device");
    }
  };

  // Check if user is admin
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-6">
            <ShieldX className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="font-heading text-2xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access the admin panel. Only system administrators can manage products and bundles.
          </p>
          <Button onClick={() => navigate("/dashboard")} className="bg-white text-black hover:bg-gray-200">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
      />

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-white/5 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="font-heading text-xl font-semibold">Admin Panel</h1>
                <p className="text-sm text-muted-foreground">Manage products, bundles, and pricing</p>
              </div>
            </div>

            <Button
              onClick={handleSeedData}
              disabled={seeding}
              variant="outline"
              className="border-border"
              data-testid="seed-data-btn"
            >
              {seeding ? (
                <div className="w-4 h-4 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Seed Sample Data
            </Button>
          </div>
        </header>

        <main className="p-6 space-y-8">
          {/* Tabs */}
          <div className="flex gap-4 border-b border-border overflow-x-auto">
            <button
              onClick={() => setActiveTab("requests")}
              className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "requests"
                  ? "border-[#007AFF] text-[#007AFF]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-requests"
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Offer Requests ({offerRequests.length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab("bundles")}
              className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "bundles"
                  ? "border-[#007AFF] text-[#007AFF]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-bundles"
            >
              Bundles ({bundles.length})
            </button>
            <button
              onClick={() => setActiveTab("packages")}
              className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "packages"
                  ? "border-[#007AFF] text-[#007AFF]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-packages"
            >
              Support Packages ({packages.length})
            </button>
            <button
              onClick={() => setActiveTab("service-requests")}
              className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "service-requests"
                  ? "border-[#007AFF] text-[#007AFF]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-service-requests"
            >
              <span className="flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Service Requests ({serviceRequests.length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab("devices")}
              className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "devices"
                  ? "border-[#007AFF] text-[#007AFF]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-devices"
            >
              <span className="flex items-center gap-2">
                <Cpu className="w-4 h-4" />
                Devices / SKUs ({devices.filter(d => d.is_active).length})
              </span>
            </button>
          </div>

          {/* Offer Requests Tab */}
          {activeTab === "requests" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-lg">Customer Offer Requests</h2>
                <p className="text-sm text-muted-foreground">
                  {offerRequests.length} total request{offerRequests.length !== 1 ? 's' : ''}
                </p>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-24 rounded-lg" />
                  ))}
                </div>
              ) : offerRequests.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-xl">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No offer requests yet</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    When customers request offers, they'll appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {offerRequests.map((req) => (
                    <div
                      key={req.request_id}
                      className="bg-card border border-border rounded-xl p-5"
                      data-testid={`request-row-${req.request_id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        {/* Customer Info */}
                        <div className="flex-1 space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-heading font-semibold text-lg">{req.user_name}</h3>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <Mail className="w-3.5 h-3.5" />
                                <span>{req.user_email}</span>
                              </div>
                              {req.company_name && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                  <Building2 className="w-3.5 h-3.5" />
                                  <span>{req.company_name}</span>
                                </div>
                              )}
                            </div>
                            <Select
                              value={req.status}
                              onValueChange={async (newStatus) => {
                                try {
                                  await axios.patch(`${API}/my-requests/${req.request_id}/status`, 
                                    { status: newStatus },
                                    { withCredentials: true }
                                  );
                                  toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
                                  fetchData();
                                } catch (error) {
                                  toast.error("Failed to update status");
                                }
                              }}
                            >
                              <SelectTrigger className="w-[140px]" data-testid={`status-select-${req.request_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_review">In Review</SelectItem>
                                <SelectItem value="quote_sent">Quote Sent</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Item Info - handles both new device format and old bundle format */}
                          <div className="bg-secondary/50 rounded-lg p-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm text-muted-foreground">
                                  {req.device_id ? 'Device' : 'Bundle'}
                                </p>
                                <p className="font-medium">{req.item_name || req.bundle_name}</p>
                                {req.quantity > 1 && (
                                  <p className="text-sm text-muted-foreground">Qty: {req.quantity}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-[#007AFF] font-heading font-bold">
                                  ₹{(req.subtotal || req.bundle_price)?.toLocaleString()}
                                </p>
                                {req.savings > 0 && (
                                  <p className="text-xs text-[#00FF88]">
                                    Save ₹{req.savings?.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* MDM/EPM if included */}
                          {req.include_mdm && (
                            <div className="bg-secondary/50 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Device Management (MDM/EPM)</p>
                              <p className="text-[#007AFF] font-heading">
                                ₹{req.mdm_monthly_cost?.toLocaleString()}/month
                              </p>
                            </div>
                          )}
                          
                          {/* Delivery Location */}
                          {req.delivery_location && (
                            <div className="bg-secondary/50 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Delivery</p>
                              <p className="font-medium">
                                {req.delivery_location === 'dubai' ? 'Dubai' : 
                                 req.delivery_location === 'other_uae' ? 'Other UAE Emirates' : 'Other Location'}
                                {req.delivery_days && ` (${req.delivery_days} days)`}
                              </p>
                            </div>
                          )}
                          
                          {/* Support Package if selected */}
                          {req.support_package_name && (
                            <div className="bg-secondary/50 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Support Package</p>
                              <p className="font-medium">{req.support_package_name}</p>
                              <p className="text-[#007AFF] font-heading">
                                ₹{req.support_package_price?.toLocaleString()}/mo
                              </p>
                            </div>
                          )}
                          
                          {/* Notes if any */}
                          {req.notes && (
                            <div className="bg-secondary/50 rounded-lg p-3">
                              <p className="text-sm text-muted-foreground">Customer Notes</p>
                              <p className="text-sm">{req.notes}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Timestamp & Actions */}
                        <div className="flex flex-col items-end gap-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{new Date(req.created_at).toLocaleDateString('en-AE', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</span>
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">
                            #{req.request_id}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bundles Tab */}
          {activeTab === "bundles" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-lg">Hardware Bundles</h2>
                <Dialog open={bundleDialogOpen} onOpenChange={setBundleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#007AFF] hover:bg-[#0055CC]" data-testid="add-bundle-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Bundle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle>Create New Bundle</DialogTitle>
                      <DialogDescription>
                        Add a new hardware bundle to the catalog
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="bundle-name">Name</Label>
                        <Input
                          id="bundle-name"
                          value={bundleForm.name}
                          onChange={(e) => setBundleForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="MacBook Pro Business Bundle"
                          data-testid="bundle-name-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="bundle-desc">Description</Label>
                        <Textarea
                          id="bundle-desc"
                          value={bundleForm.description}
                          onChange={(e) => setBundleForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Complete business setup..."
                          data-testid="bundle-desc-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="bundle-category">Category</Label>
                        <Select
                          value={bundleForm.category}
                          onValueChange={(value) => setBundleForm(prev => ({ ...prev, category: value }))}
                        >
                          <SelectTrigger id="bundle-category" data-testid="bundle-category-select">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(cat => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="bundle-price">Base Price (AED)</Label>
                          <Input
                            id="bundle-price"
                            type="number"
                            value={bundleForm.base_price}
                            onChange={(e) => setBundleForm(prev => ({ ...prev, base_price: e.target.value }))}
                            placeholder="4299"
                            data-testid="bundle-price-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="bundle-market">Market Price (AED)</Label>
                          <Input
                            id="bundle-market"
                            type="number"
                            value={bundleForm.market_price}
                            onChange={(e) => setBundleForm(prev => ({ ...prev, market_price: e.target.value }))}
                            placeholder="5199"
                            data-testid="bundle-market-input"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="bundle-lead">Lead Time (days)</Label>
                          <Input
                            id="bundle-lead"
                            type="number"
                            value={bundleForm.lead_time_days}
                            onChange={(e) => setBundleForm(prev => ({ ...prev, lead_time_days: e.target.value }))}
                            placeholder="15"
                            data-testid="bundle-lead-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="bundle-delivery">Delivery Days</Label>
                          <Input
                            id="bundle-delivery"
                            value={bundleForm.delivery_days}
                            onChange={(e) => setBundleForm(prev => ({ ...prev, delivery_days: e.target.value }))}
                            placeholder="5-8"
                            data-testid="bundle-delivery-input"
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="bundle-recommended"
                          checked={bundleForm.is_recommended}
                          onCheckedChange={(checked) => setBundleForm(prev => ({ ...prev, is_recommended: checked }))}
                          data-testid="bundle-recommended-switch"
                        />
                        <Label htmlFor="bundle-recommended">Mark as Recommended</Label>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setBundleDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateBundle}
                        className="bg-[#007AFF] hover:bg-[#0055CC]"
                        data-testid="save-bundle-btn"
                      >
                        Create Bundle
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-20 rounded-lg" />
                  ))}
                </div>
              ) : bundles.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-xl">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No bundles yet</p>
                  <Button onClick={handleSeedData} variant="outline">
                    Seed Sample Data
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {bundles.map((bundle) => (
                    <div
                      key={bundle.bundle_id}
                      className="bg-card border border-border rounded-xl p-4 flex items-center justify-between"
                      data-testid={`bundle-row-${bundle.bundle_id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-semibold">{bundle.name}</h3>
                          {bundle.is_recommended && (
                            <span className="px-2 py-0.5 rounded-full recommended-badge text-xs">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{bundle.category}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="font-bold font-heading">₹{bundle.base_price.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">
                            Market: ₹{bundle.market_price.toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Packages Tab */}
          {activeTab === "packages" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-lg">Support Packages</h2>
                <Button className="bg-[#007AFF] hover:bg-[#0055CC]" data-testid="add-package-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Package
                </Button>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-20 rounded-lg" />
                  ))}
                </div>
              ) : packages.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-xl">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No packages yet</p>
                  <Button onClick={handleSeedData} variant="outline">
                    Seed Sample Data
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.package_id}
                      className="bg-card border border-border rounded-xl p-4 flex items-center justify-between"
                      data-testid={`package-row-${pkg.package_id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-semibold">{pkg.name}</h3>
                          {pkg.is_recommended && (
                            <span className="px-2 py-0.5 rounded-full recommended-badge text-xs">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{pkg.description}</p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="font-bold font-heading">₹{pkg.monthly_price}/mo</p>
                          <p className="text-xs text-muted-foreground">
                            {pkg.features.length} features
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Service Requests Tab */}
          {activeTab === "service-requests" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-lg">Customer Service Requests</h2>
                <p className="text-sm text-muted-foreground">
                  {serviceRequests.length} total request{serviceRequests.length !== 1 ? "s" : ""}
                </p>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <div key={i} className="skeleton h-24 rounded-lg" />)}
                </div>
              ) : serviceRequests.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-xl">
                  <Wrench className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No service requests yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {serviceRequests.map(sr => {
                    const srStatusColors = {
                      open: { label: "Open", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
                      in_progress: { label: "In Progress", color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
                      resolved: { label: "Resolved", color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/20" }
                    };
                    const cfg = srStatusColors[sr.status] || srStatusColors.open;
                    return (
                      <div
                        key={sr.sr_id}
                        className="bg-card border border-border rounded-xl p-5"
                        data-testid={`sr-row-${sr.sr_id}`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <h3 className="font-heading font-semibold">{sr.user_name}</h3>
                              <span className="text-xs text-muted-foreground">·</span>
                              <span className="text-sm text-muted-foreground">{sr.user_email}</span>
                            </div>
                            <div className="bg-secondary/50 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground mb-1">Asset</p>
                              <p className="font-medium text-sm">{sr.asset_name}</p>
                            </div>
                            <div className="bg-secondary/50 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground mb-1">Issue</p>
                              <p className="text-sm">{sr.issue_description}</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-3">
                            <Select
                              value={sr.status}
                              onValueChange={async (newStatus) => {
                                try {
                                  await axios.patch(`${API}/admin/service-requests/${sr.sr_id}/status`,
                                    { status: newStatus },
                                    { withCredentials: true }
                                  );
                                  toast.success("Status updated");
                                  fetchData();
                                } catch {
                                  toast.error("Failed to update status");
                                }
                              }}
                            >
                              <SelectTrigger className="w-[140px]" data-testid={`sr-status-${sr.sr_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{new Date(sr.created_at).toLocaleDateString("en-AE", { year: "numeric", month: "short", day: "numeric" })}</span>
                            </div>
                            <p className="text-xs font-mono text-muted-foreground">#{sr.sr_id}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Devices / SKUs Tab */}
          {activeTab === "devices" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading font-semibold text-lg">Devices & SKUs</h2>
                  <p className="text-sm text-muted-foreground">{devices.filter(d => d.is_active).length} active · {devices.filter(d => !d.is_active).length} inactive</p>
                </div>
                <Button onClick={() => openDeviceDialog()} className="bg-[#007AFF] hover:bg-[#0055CC]" data-testid="add-device-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Device
                </Button>
              </div>

              {/* Group by category */}
              {["laptop", "desktop", "tablet", "phone", "accessory"].map(cat => {
                const catDevices = devices.filter(d => d.category === cat);
                if (catDevices.length === 0) return null;
                const catLabels = { laptop: "Laptops", desktop: "Desktops", tablet: "Tablets", phone: "Phones", accessory: "Accessories" };
                return (
                  <div key={cat}>
                    <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{catLabels[cat] || cat} ({catDevices.length})</h3>
                    <div className="space-y-2">
                      {catDevices.map(device => (
                        <div
                          key={device.device_id}
                          className={`bg-card border rounded-xl px-4 py-3 flex items-center gap-4 ${!device.is_active ? "opacity-40 border-border/40" : "border-border"}`}
                          data-testid={`device-row-${device.device_id}`}
                        >
                          {device.image_url && (
                            <img src={device.image_url} alt={device.name} className="w-12 h-12 object-cover rounded-lg shrink-0 bg-secondary" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-heading font-semibold text-sm">{device.name}</p>
                              {device.brand && <span className="text-xs text-muted-foreground">{device.brand}</span>}
                              {!device.is_active && <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full">Inactive</span>}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-0.5">
                              <span>OneIT: ₹{device.base_price?.toLocaleString()}</span>
                              <span>Market: ₹{device.market_price?.toLocaleString()}</span>
                              {device.apple_care_price > 0 && <span>AC+: ₹{device.apple_care_price?.toLocaleString()}</span>}
                              {device.mdm_compatible && <span>MDM: ₹{device.mdm_monthly_price}/mo</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button size="sm" variant="outline" onClick={() => openDeviceDialog(device)} data-testid={`edit-device-${device.device_id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleDevice(device)}
                              className={device.is_active ? "text-red-400 hover:text-red-300" : "text-green-400 hover:text-green-300"}
                              data-testid={`toggle-device-${device.device_id}`}
                            >
                              {device.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Device Add/Edit Dialog */}
      <Dialog open={deviceDialogOpen} onOpenChange={setDeviceDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDevice ? "Edit Device" : "Add New Device"}</DialogTitle>
            <DialogDescription>{editingDevice ? `Editing: ${editingDevice.name}` : "Add a new device SKU to the catalog"}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Device Name *</Label>
              <Input value={deviceForm.name} onChange={e => setDeviceForm({...deviceForm, name: e.target.value})} placeholder='e.g. MacBook Pro 14" M4 Pro' className="mt-1" data-testid="device-form-name" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={deviceForm.category} onValueChange={v => setDeviceForm({...deviceForm, category: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="laptop">Laptop</SelectItem>
                  <SelectItem value="desktop">Desktop</SelectItem>
                  <SelectItem value="tablet">Tablet</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="accessory">Accessory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Brand</Label>
              <Input value={deviceForm.brand} onChange={e => setDeviceForm({...deviceForm, brand: e.target.value})} placeholder="Apple / Dell / Samsung" className="mt-1" />
            </div>
            <div>
              <Label>OneIT Price (AED) *</Label>
              <Input type="number" value={deviceForm.base_price} onChange={e => setDeviceForm({...deviceForm, base_price: e.target.value})} placeholder="3409" className="mt-1" data-testid="device-form-price" />
            </div>
            <div>
              <Label>Market Price (AED) *</Label>
              <Input type="number" value={deviceForm.market_price} onChange={e => setDeviceForm({...deviceForm, market_price: e.target.value})} placeholder="3799" className="mt-1" />
            </div>
            <div>
              <Label>Apple Care+ Price (AED)</Label>
              <Input type="number" value={deviceForm.apple_care_price} onChange={e => setDeviceForm({...deviceForm, apple_care_price: e.target.value})} placeholder="533 (leave blank if N/A)" className="mt-1" />
            </div>
            <div>
              <Label>MDM Monthly Price (AED)</Label>
              <Input type="number" value={deviceForm.mdm_monthly_price} onChange={e => setDeviceForm({...deviceForm, mdm_monthly_price: e.target.value})} placeholder="35" className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Image URL</Label>
              <Input value={deviceForm.image_url} onChange={e => setDeviceForm({...deviceForm, image_url: e.target.value})} placeholder="https://..." className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea value={deviceForm.description} onChange={e => setDeviceForm({...deviceForm, description: e.target.value})} rows={2} className="mt-1 resize-none bg-secondary border-border" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={deviceForm.mdm_compatible} onCheckedChange={v => setDeviceForm({...deviceForm, mdm_compatible: v})} />
              <Label>MDM Compatible</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={deviceForm.is_active} onCheckedChange={v => setDeviceForm({...deviceForm, is_active: v})} />
              <Label>Active (visible to users)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeviceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveDevice} className="bg-[#007AFF] hover:bg-[#0055CC]" data-testid="save-device-btn">
              {editingDevice ? "Save Changes" : "Add Device"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
